/** What the battle screen needs from the shared renderer modules.
 *
 * `packages/render/src/atlas.ts` and `packages/render/src/coords.ts` are owned
 * by the world-renderer agent and did not exist when this was written, so the
 * battle screen depends on the *shape* they were specified to have rather than
 * on the modules themselves. Structural typing means the real `Atlas` drops in
 * with no edit here the moment it lands; if its method names differ, this file
 * is the single place that changes.
 */

import type { Texture, Renderer } from "pixi.js";

/** One packed frame, straight out of `assets.json`. */
export interface AtlasFrame {
  readonly sheet: string;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  /**
   * The ground line, in the *trimmed* frame's own pixels: bottom-centre of the
   * opaque box, not the corner of the bounding box. Drawing from the bounding
   * box is the single most common way a 2D battlefield reads as flat.
   */
  readonly anchorX: number;
  readonly anchorY: number;
  readonly trimX: number;
  readonly trimY: number;
  readonly srcW: number;
  readonly srcH: number;
}

export interface AtlasLike {
  texture(key: string): Texture;
  /** The real atlas uploads every sheet into a renderer and releases the CPU
   *  copies it can (`Atlas.prepare`); a hand-rolled one has nothing to release. */
  prepare?(renderer: Renderer, groups: readonly string[]): Promise<void>;
  /** The real atlas returns `null` for a miss; a hand-rolled one may return
   *  `undefined`. Both mean "no such key". */
  frame(key: string): AtlasFrame | null | undefined;
}

/**
 * Pixel art must sample NEAREST or the whole point of drawing it at an integer
 * scale is lost. The atlas is not ours, so rather than reach into its texture
 * sources this wraps it: every texture handed out gets its source's filter set
 * once, which is idempotent and costs a property write per key.
 */
export function nearestFiltered(atlas: AtlasLike): AtlasLike {
  const prepare = atlas.prepare;
  return {
    ...(prepare === undefined ? {} : { prepare: (r: Renderer, g: readonly string[]) => prepare.call(atlas, r, g) }),
    texture(key: string): Texture {
      const t = atlas.texture(key);
      t.source.scaleMode = "nearest";
      return t;
    },
    frame(key: string): AtlasFrame | null | undefined {
      return atlas.frame(key);
    },
  };
}

/** True when the atlas can draw this key. */
export function hasKey(atlas: AtlasLike, key: string): boolean {
  return atlas.frame(key) != null;
}

/**
 * Normalised anchor for a frame, ready for `Sprite.anchor`. Falls back to
 * bottom-centre, which is the right guess for anything that stands on ground.
 */
export function anchorOf(frame: AtlasFrame | null | undefined): { readonly x: number; readonly y: number } {
  if (frame == null || frame.w === 0 || frame.h === 0) return { x: 0.5, y: 1 };
  return { x: frame.anchorX / frame.w, y: frame.anchorY / frame.h };
}
